import { useEffect, useRef } from 'react';
import { CommandRegistry, initializeCommandRegistry } from './commandRegistry';
import { DEFAULT_COMMAND_HOTKEYS } from './hotkeyConfig';
import { useWebsocketStore } from '../stores/websocketStore/websocketProvider';

const isEditableTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable === true
  );
};

const normalizeCombo = (event) => {
  const parts = [];
  if (event.ctrlKey) parts.push('ctrl');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey) parts.push('shift');
  if (event.metaKey) parts.push('meta');
  // Match digits by physical key so macOS Option+digit (which produces
  // special characters in event.key) still resolves.
  const digitMatch = /^(?:Digit|Numpad)([0-9])$/.exec(event.code ?? '');
  const key = digitMatch ? digitMatch[1] : `${event.key}`.toLowerCase();
  parts.push(key);
  return parts.join('+');
};

// Installs the keyboard layer for Trade Plan selection and commands:
// - Alt/Option+1..9 selects the nth trade plan in current sort order
// - Tab / Shift+Tab cycles selection (only while a trade plan is selected,
//   so normal browser tabbing works when nothing is selected)
// - Escape clears selection
// - Command hotkeys (hotkeyConfig) execute against the selected trade plan
export const useTradePlanSelectionHotkeys = (orderedTradePlanIds) => {
  const store = useWebsocketStore();
  const orderedIdsRef = useRef(orderedTradePlanIds);
  orderedIdsRef.current = orderedTradePlanIds;

  useEffect(() => {
    initializeCommandRegistry(store);
    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;

      const selectionStore = store.tradePlanSelectionStore;
      const orderedIds = orderedIdsRef.current ?? [];
      const combo = normalizeCombo(event);

      if (event.key === 'Escape') {
        if (selectionStore.selectedTradePlanId !== null) {
          selectionStore.clearSelection();
          event.preventDefault();
        }
        return;
      }

      const ordinalMatch = /^alt\+([1-9])$/.exec(combo);
      if (ordinalMatch) {
        selectionStore.selectByOrdinal(orderedIds, Number(ordinalMatch[1]));
        event.preventDefault();
        return;
      }

      if (
        event.key === 'Tab' &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        selectionStore.selectedTradePlanId !== null
      ) {
        selectionStore.selectAdjacent(orderedIds, event.shiftKey ? -1 : 1);
        event.preventDefault();
        return;
      }

      const commandName = DEFAULT_COMMAND_HOTKEYS[combo];
      if (commandName) {
        CommandRegistry.execute(commandName);
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [store]);
};
